/**
 * Social Page - Player profile, friends, referrals, and activity
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  getFriends,
  getFriendRequests,
  sendFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  removeFriend,
  getFriendsActivity,
  FriendRequest,
  FriendActivity,
} from '../../api/friends';
import {
  getProfile,
  registerHandle,
  updateDisplayPreference,
  lookupByHandle,
  PlayerProfile,
} from '../../api/profile';
import {
  getReferralStats,
  getReferralList,
  getShareableReferralUrl,
  ReferralStats,
  ReferralEntry,
} from '../../api/referrals';
import { usePresenceStore } from '../../state/presenceStore';
import { shortKey, formatLamports } from '../../utils/format';

type TabId = 'friends' | 'requests' | 'activity' | 'referrals';

export const FriendsPage: React.FC = () => {
  const navigate = useNavigate();
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();

  // Profile state
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [handleInput, setHandleInput] = useState('');
  const [handleSaving, setHandleSaving] = useState(false);

  // Friends state
  const [friends, setFriends] = useState<string[]>([]);
  const [displayNames, setDisplayNames] = useState<Record<string, string>>({});
  const [pendingRequests, setPendingRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [activity, setActivity] = useState<FriendActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [addFriendInput, setAddFriendInput] = useState('');
  const [activeTab, setActiveTab] = useState<TabId>('friends');

  // Referral state
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [referrals, setReferrals] = useState<ReferralEntry[]>([]);
  const [referralCopied, setReferralCopied] = useState(false);
  const [shareAsHandle, setShareAsHandle] = useState(false);

  const shareIdentifier =
    shareAsHandle && profile?.handle ? profile.handle : (referralStats?.referralCode ?? '');

  const { isOnline } = usePresenceStore();

  const fetchData = useCallback(async () => {
    if (!wallet) return;
    try {
      const [profileData, friendsData, requestsData, activityData, statsData, referralData] =
        await Promise.all([
          getProfile(wallet),
          getFriends(wallet),
          getFriendRequests(wallet),
          getFriendsActivity(wallet),
          getReferralStats(wallet).catch(() => null),
          getReferralList(wallet).catch(() => []),
        ]);
      setProfile(profileData);
      setFriends(friendsData.friends);
      setDisplayNames(friendsData.displayNames ?? {});
      setPendingRequests(requestsData.pending);
      setSentRequests(requestsData.sent);
      setActivity(activityData.activity);
      setReferralStats(statsData);
      setReferrals(referralData);
    } catch (error) {
      console.error('Failed to fetch social data:', error);
      toast.error('Failed to load social data');
    } finally {
      setLoading(false);
    }
  }, [wallet]);

  useEffect(() => {
    if (!wallet) {
      navigate('/');
      return;
    }
    fetchData();
  }, [wallet, navigate, fetchData]);

  const handleRegisterHandle = async () => {
    if (!wallet || !handleInput.trim()) return;

    setHandleSaving(true);
    try {
      const result = await registerHandle(wallet, handleInput.trim());
      if (result.success) {
        toast.success('Handle registered!');
        setHandleInput('');
        const updated = await getProfile(wallet);
        setProfile(updated);
      } else {
        toast.error(result.error ?? 'Failed to register handle');
      }
    } catch {
      toast.error('Failed to register handle');
    } finally {
      setHandleSaving(false);
    }
  };

  const handleToggleDisplay = async () => {
    if (!wallet || !profile) return;

    const newPref = profile.displayPreference === 'handle' ? 'wallet' : 'handle';
    try {
      const result = await updateDisplayPreference(wallet, newPref);
      if (result.success) {
        setProfile({ ...profile, displayPreference: newPref });
        toast.success(
          `Now displaying as ${newPref === 'handle' ? profile.handle : shortKey(wallet)}`,
        );
      }
    } catch {
      toast.error('Failed to update display preference');
    }
  };

  const handleSendRequest = async () => {
    if (!wallet || !addFriendInput.trim()) return;

    const input = addFriendInput.trim();
    let targetWallet = input;

    // If input looks like a handle (not a full wallet address), resolve it
    if (input.length < 32) {
      try {
        const result = await lookupByHandle(input);
        if (!result.wallet) {
          toast.error(`No player found with handle "${input}"`);
          return;
        }
        targetWallet = result.wallet;
      } catch {
        toast.error('Failed to look up handle');
        return;
      }
    }

    try {
      await sendFriendRequest(wallet, targetWallet);
      toast.success('Friend request sent!');
      setAddFriendInput('');
      const requestsData = await getFriendRequests(wallet);
      setSentRequests(requestsData.sent);
    } catch {
      toast.error('Failed to send friend request');
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    if (!wallet) return;

    try {
      await acceptFriendRequest(requestId, wallet);
      toast.success('Friend request accepted!');
      const [friendsData, requestsData] = await Promise.all([
        getFriends(wallet),
        getFriendRequests(wallet),
      ]);
      setFriends(friendsData.friends);
      setDisplayNames(friendsData.displayNames ?? {});
      setPendingRequests(requestsData.pending);
    } catch {
      toast.error('Failed to accept request');
    }
  };

  const handleDeclineRequest = async (requestId: string) => {
    if (!wallet) return;

    try {
      await declineFriendRequest(requestId, wallet);
      toast.success('Friend request declined');
      const requestsData = await getFriendRequests(wallet);
      setPendingRequests(requestsData.pending);
    } catch {
      toast.error('Failed to decline request');
    }
  };

  const handleRemoveFriend = async (friendWallet: string) => {
    if (!wallet) return;

    try {
      await removeFriend(wallet, friendWallet);
      toast.success('Friend removed');
      setFriends(friends.filter((f) => f !== friendWallet));
    } catch {
      toast.error('Failed to remove friend');
    }
  };

  // Referral handlers
  const handleCopyReferralCode = () => {
    if (!shareIdentifier) return;
    navigator.clipboard.writeText(shareIdentifier);
    toast.success('Copied!');
  };

  const handleCopyReferralLink = () => {
    if (!shareIdentifier) return;
    const url = getShareableReferralUrl(shareIdentifier);
    navigator.clipboard.writeText(url);
    setReferralCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setReferralCopied(false), 2000);
  };

  const handleShareReferralTwitter = () => {
    if (!shareIdentifier) return;
    const url = getShareableReferralUrl(shareIdentifier);
    const text = encodeURIComponent(
      'Join me on Solante - the roguelike pvp deck-builder game on Solana! Use my referral link:',
    );
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`,
      '_blank',
    );
  };

  const getActivityText = (act: FriendActivity): string => {
    switch (act.type) {
      case 'match_won':
        return `Won a match with ${act.data.score ?? '?'} points`;
      case 'match_lost':
        return `Lost a match`;
      case 'level_up':
        return `Reached level ${act.data.level ?? '?'}`;
      case 'rank_up':
        return `Ranked up to ${act.data.rank ?? '?'}`;
      case 'achievement':
        return `Unlocked "${act.data.name ?? 'achievement'}"`;
      case 'streak':
        return `On a ${act.data.streak ?? '?'}-win streak!`;
      default:
        return 'Activity';
    }
  };

  /** Resolve a wallet or request wallet to a display name */
  const resolveName = (w: string) => displayNames[w] || shortKey(w);

  if (!wallet) return null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-white/60">Loading...</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen py-8 px-4"
    >
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="neon-text font-display text-3xl font-bold">SOCIAL</h1>
          <p className="text-white/50 text-sm mt-1">Manage your identity and friends</p>
        </div>

        {/* Profile Section */}
        <div className="arcade-panel pixel-corners rounded-xl p-5 space-y-4">
          <h2 className="text-sm uppercase tracking-widest text-white/50">Your Profile</h2>

          {/* Current display */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/40">Showing as</span>
            <span className="font-mono text-sm text-arcade-cyan">
              {profile?.handle && profile.displayPreference === 'handle'
                ? profile.handle
                : shortKey(wallet)}
            </span>
          </div>

          {/* Handle registration */}
          <div className="space-y-2">
            <label className="text-xs text-white/50">
              {profile?.handle ? 'Update handle' : 'Register handle'}
            </label>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
              <input
                type="text"
                value={handleInput}
                onChange={(e) => setHandleInput(e.target.value)}
                placeholder={profile?.handle ?? 'Choose a username...'}
                maxLength={20}
                className="flex-1 rounded-lg border border-white/10 bg-black/50 px-4 py-2 text-sm text-white placeholder-white/30 focus:border-neon-400 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleRegisterHandle}
                disabled={!handleInput.trim() || handleSaving}
                className="arcade-btn px-4 py-2 text-sm disabled:opacity-50 sm:w-auto"
              >
                {handleSaving ? 'Saving...' : profile?.handle ? 'Update' : 'Register'}
              </button>
            </div>
            <p className="text-[10px] text-white/30">
              3-20 characters, letters, numbers, and underscores only.
              {profile?.handle && profile.handleChanges > 0 && (
                <> Handle changes will cost 5 tokens after TGE.</>
              )}
            </p>
          </div>

          {/* Display preference toggle */}
          {profile?.handle && (
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/10">
              <div className="min-w-0">
                <p className="text-xs text-white/60">Display preference</p>
                <p className="text-[10px] text-white/30">Choose what other players see</p>
              </div>
              <button
                type="button"
                onClick={handleToggleDisplay}
                className={`shrink-0 truncate max-w-[50%] px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  profile.displayPreference === 'handle'
                    ? 'bg-arcade-cyan/20 text-arcade-cyan border border-arcade-cyan/30'
                    : 'bg-white/10 text-white/60 border border-white/10'
                }`}
              >
                {profile.displayPreference === 'handle'
                  ? `Handle: ${profile.handle}`
                  : `Wallet: ${shortKey(wallet)}`}
              </button>
            </div>
          )}
        </div>

        {/* Add Friend */}
        <div className="arcade-panel pixel-corners rounded-xl p-4">
          <h2 className="text-sm uppercase tracking-widest text-white/50 mb-3">Add Friend</h2>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            <input
              type="text"
              value={addFriendInput}
              onChange={(e) => setAddFriendInput(e.target.value)}
              placeholder="Enter wallet address or handle..."
              className="flex-1 rounded-lg border border-white/10 bg-black/50 px-4 py-2 text-sm text-white placeholder-white/30 focus:border-neon-400 focus:outline-none"
            />
            <button
              type="button"
              onClick={handleSendRequest}
              disabled={!addFriendInput.trim()}
              className="arcade-btn px-4 py-2 text-sm disabled:opacity-50 sm:w-auto"
            >
              Send Request
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mb-1">
          {(['friends', 'requests', 'activity', 'referrals'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition ${
                activeTab === tab
                  ? 'bg-neon-500/20 text-neon-400 border border-neon-400/30'
                  : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
              }`}
            >
              {tab === 'friends'
                ? `Friends (${friends.length})`
                : tab === 'requests'
                  ? `Requests (${pendingRequests.length})`
                  : tab === 'referrals'
                    ? 'Referrals'
                    : 'Activity'}
            </button>
          ))}
        </div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="arcade-panel pixel-corners rounded-xl p-4 min-h-[300px]"
        >
          {activeTab === 'friends' && (
            <div className="space-y-2">
              {friends.length === 0 ? (
                <p className="text-center text-white/40 py-8">
                  No friends yet. Send a friend request to get started!
                </p>
              ) : (
                friends.map((friend) => (
                  <div
                    key={friend}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/5 px-4 py-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className={`w-2 h-2 shrink-0 rounded-full ${
                          isOnline(friend) ? 'bg-green-400' : 'bg-white/30'
                        }`}
                      />
                      <span className="truncate font-mono text-sm text-white/80">
                        {resolveName(friend)}
                      </span>
                      {displayNames[friend] && (
                        <span className="shrink-0 text-[10px] text-white/30">
                          {shortKey(friend)}
                        </span>
                      )}
                      <span className="shrink-0 text-[10px] text-white/40">
                        {isOnline(friend) ? 'Online' : 'Offline'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFriend(friend)}
                      className="shrink-0 text-xs text-red-400/60 hover:text-red-400 transition"
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="space-y-4">
              {/* Pending Requests */}
              <div>
                <h3 className="text-xs uppercase tracking-wide text-white/50 mb-2">
                  Pending ({pendingRequests.length})
                </h3>
                {pendingRequests.length === 0 ? (
                  <p className="text-xs text-white/40">No pending requests</p>
                ) : (
                  <div className="space-y-2">
                    {pendingRequests.map((req) => (
                      <div
                        key={req.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/5 px-4 py-3"
                      >
                        <span className="min-w-0 truncate font-mono text-sm text-white/80">
                          {resolveName(req.fromWallet)}
                        </span>
                        <div className="flex shrink-0 gap-2">
                          <button
                            type="button"
                            onClick={() => handleAcceptRequest(req.id)}
                            className="px-3 py-1 rounded bg-green-500/20 text-green-400 text-xs hover:bg-green-500/30"
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeclineRequest(req.id)}
                            className="px-3 py-1 rounded bg-red-500/20 text-red-400 text-xs hover:bg-red-500/30"
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sent Requests */}
              <div>
                <h3 className="text-xs uppercase tracking-wide text-white/50 mb-2">
                  Sent ({sentRequests.length})
                </h3>
                {sentRequests.length === 0 ? (
                  <p className="text-xs text-white/40">No sent requests</p>
                ) : (
                  <div className="space-y-2">
                    {sentRequests.map((req) => (
                      <div
                        key={req.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/5 px-4 py-3"
                      >
                        <span className="min-w-0 truncate font-mono text-sm text-white/80">
                          {req.toWallet ? resolveName(req.toWallet) : 'Unknown'}
                        </span>
                        <span
                          className={`text-xs ${
                            req.status === 'pending'
                              ? 'text-amber-400'
                              : req.status === 'accepted'
                                ? 'text-green-400'
                                : 'text-red-400'
                          }`}
                        >
                          {req.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-2">
              {activity.length === 0 ? (
                <p className="text-center text-white/40 py-8">No activity from friends yet</p>
              ) : (
                activity.map((act) => (
                  <div
                    key={act.id}
                    className="flex items-center justify-between gap-2 rounded-lg bg-white/5 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <span className="font-mono text-xs text-white/60">
                        {resolveName(act.wallet)}
                      </span>
                      <p className="truncate text-sm text-white/80">{getActivityText(act)}</p>
                    </div>
                    <span className="text-[10px] text-white/40">
                      {new Date(act.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'referrals' && (
            <div className="space-y-4">
              {referralStats ? (
                <>
                  {/* Share mode toggle */}
                  {profile?.handle && (
                    <div className="flex items-center gap-3 rounded-lg bg-white/5 px-4 py-2.5">
                      <span className="text-xs text-white/50">Share as</span>
                      <button
                        type="button"
                        onClick={() => setShareAsHandle(false)}
                        className={`rounded px-3 py-1 text-xs font-semibold transition ${
                          !shareAsHandle
                            ? 'border border-arcade-cyan/30 bg-arcade-cyan/20 text-arcade-cyan'
                            : 'border border-white/10 bg-white/10 text-white/50 hover:bg-white/15'
                        }`}
                      >
                        Code
                      </button>
                      <button
                        type="button"
                        onClick={() => setShareAsHandle(true)}
                        className={`rounded px-3 py-1 text-xs font-semibold transition ${
                          shareAsHandle
                            ? 'border border-arcade-cyan/30 bg-arcade-cyan/20 text-arcade-cyan'
                            : 'border border-white/10 bg-white/10 text-white/50 hover:bg-white/15'
                        }`}
                      >
                        Handle
                      </button>
                    </div>
                  )}

                  {/* Code/Handle + Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="w-full sm:w-auto sm:flex-1 min-w-0 overflow-hidden truncate rounded-lg bg-black/50 px-4 py-2.5 text-center font-mono text-base sm:text-lg tracking-wider text-arcade-cyan">
                      {shareIdentifier}
                    </div>
                    <button
                      type="button"
                      onClick={handleCopyReferralCode}
                      className="btn-press rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-semibold text-white transition hover:bg-white/20"
                    >
                      {shareAsHandle && profile?.handle ? 'Copy Handle' : 'Copy Code'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCopyReferralLink}
                      className={`btn-press rounded-lg border px-4 py-2.5 text-xs font-semibold transition ${
                        referralCopied
                          ? 'border-arcade-green/50 bg-arcade-green/20 text-arcade-green'
                          : 'border-white/20 bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {referralCopied ? 'Copied!' : 'Copy Link'}
                    </button>
                    <button
                      type="button"
                      onClick={handleShareReferralTwitter}
                      className="btn-press rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-2.5 text-xs font-semibold text-blue-300 transition hover:bg-blue-500/20"
                    >
                      Share on X
                    </button>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <div className="rounded-lg bg-white/5 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-white/40">
                        Total Referrals
                      </p>
                      <p className="text-xl font-bold text-white">{referralStats.totalReferrals}</p>
                    </div>
                    <div className="rounded-lg border border-arcade-cyan/20 bg-arcade-cyan/5 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-white/40">Active</p>
                      <p className="text-xl font-bold text-arcade-cyan">
                        {referralStats.activeReferrals}
                      </p>
                    </div>
                    <div className="rounded-lg bg-white/5 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-white/40">
                        Total Earnings
                      </p>
                      <p className="text-xl font-bold text-white">
                        {formatLamports(BigInt(referralStats.totalEarnings), 'SOL')} SOL
                      </p>
                    </div>
                    <div className="rounded-lg bg-white/5 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-white/40">Pending</p>
                      <p className="text-xl font-bold text-white">
                        {formatLamports(BigInt(referralStats.pendingEarnings), 'SOL')} SOL
                      </p>
                    </div>
                  </div>

                  {/* How It Works */}
                  <div className="rounded-lg bg-white/5 p-4">
                    <p className="mb-2 text-[10px] uppercase tracking-wide text-white/40">
                      How It Works
                    </p>
                    <div className="space-y-1.5 text-xs text-white/60">
                      <p>
                        <span className="text-arcade-cyan font-bold">1.</span> Share your referral
                        code or link with friends
                      </p>
                      <p>
                        <span className="text-arcade-cyan font-bold">2.</span> When they sign up and
                        enter your code, you become their referrer
                      </p>
                      <p>
                        <span className="text-arcade-cyan font-bold">3.</span> Earn{' '}
                        <span className="text-amber-400 font-semibold">5%</span> of treasury fees
                        from every match they play — forever
                      </p>
                    </div>
                  </div>

                  {/* Referral List */}
                  {referrals.length > 0 ? (
                    <div>
                      <p className="mb-2 text-[10px] uppercase tracking-wide text-white/40">
                        Your Referrals
                      </p>
                      <div className="space-y-1.5">
                        {referrals.map((ref, i) => (
                          <div
                            key={i}
                            className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                              ref.active ? 'bg-arcade-cyan/5' : 'bg-white/5'
                            }`}
                          >
                            <div className="flex min-w-0 items-center gap-2">
                              <span
                                className={`h-2 w-2 shrink-0 rounded-full ${ref.active ? 'bg-arcade-cyan' : 'bg-white/30'}`}
                              />
                              <span className="truncate font-mono text-xs text-white/60">
                                {ref.refereeWallet.slice(0, 4)}…{ref.refereeWallet.slice(-4)}
                              </span>
                            </div>
                            <div className="flex shrink-0 items-center gap-2 text-xs">
                              <span className="text-white/40">{ref.matchesPlayed} matches</span>
                              <span className="font-semibold text-amber-400">
                                +{formatLamports(BigInt(ref.totalEarnings), 'SOL')} SOL
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-center text-xs text-white/30">
                      No referrals yet. Share your code to get started!
                    </p>
                  )}
                </>
              ) : (
                <p className="text-center text-white/40 py-8">
                  Connect your wallet to view referral program
                </p>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};
